import { resolveMediaUrl } from '@/lib/media';
import { ExternalLink, Download, Share2 } from 'lucide-react';
import { getBrandForLink } from '@/data/brandIcons';

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : { r: 0, g: 0, b: 0 };
};

const getLuminance = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
};

const isLightColor = (hex) => getLuminance(hex) > 0.5;

const darkenColor = (hex, amount = 0.3) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgb(${Math.round(r * (1 - amount))}, ${Math.round(g * (1 - amount))}, ${Math.round(b * (1 - amount))})`;
};

const lightenColor = (hex, amount = 0.15) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgb(${Math.min(255, Math.round(r + (255 - r) * amount))}, ${Math.min(255, Math.round(g + (255 - g) * amount))}, ${Math.min(255, Math.round(b + (255 - b) * amount))})`;
};

const ProfilePreview = ({ profile, links, products = [] }) => {
  if (!profile) return null;

  const bgColor = profile.background_color || '#050505';
  const isGradient = bgColor.startsWith('linear');
  const solidBg = isGradient ? (bgColor.match(/#[0-9a-fA-F]{6}/)?.[0] || '#050505') : bgColor;
  const themeColor = profile.theme_color || '#8B5CF6';
  const lightBg = isLightColor(solidBg);
  const textColor = lightBg ? darkenColor(solidBg, 0.7) : '#ffffff';
  const subtextColor = lightBg ? darkenColor(solidBg, 0.5) : 'rgba(255,255,255,0.6)';
  const cardBg = lightBg ? lightenColor(solidBg, 0.3) : 'rgba(255,255,255,0.08)';
  const borderColor = lightBg ? darkenColor(solidBg, 0.5) : 'rgba(255,255,255,0.15)';
  const activeLinks = links?.filter(l => l.is_active) || [];
  const activeProducts = products?.filter(p => p.is_active) || [];
  const catalogTitle = profile.catalog_title || (profile.catalog_type === 'services' ? 'Services' : 'Shop');

  return (
    <div className="phone-frame p-3">
      <div
        className="h-full rounded-[2rem] overflow-y-auto overflow-x-hidden"
        style={{ background: bgColor }}
      >
        <div className="px-2 pb-4">
          {/* Hero Image */}
          {profile.avatar_url && (
            <div className="pt-2">
              <div className="relative overflow-hidden" style={{ background: solidBg }}>
                <img
                  src={resolveMediaUrl(profile.avatar_url)}
                  alt=""
                  className="w-full object-contain"
                  style={{ maxHeight: '140px', mixBlendMode: 'multiply' }}
                  onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                />
                <div className="absolute inset-0 pointer-events-none" style={{
                  background: `
                    linear-gradient(to bottom, transparent 40%, ${solidBg} 95%),
                    linear-gradient(to top, transparent 70%, ${solidBg} 100%),
                    linear-gradient(to right, ${solidBg} 0%, transparent 20%, transparent 80%, ${solidBg} 100%)
                  `
                }} />
              </div>
            </div>
          )}

          {/* Brand Name */}
          <h3
            className="text-center leading-none"
            style={{
              color: textColor,
              fontSize: '1.1rem',
              fontFamily: "'Georgia', serif",
              fontWeight: 900,
              marginTop: profile.avatar_url ? '-12px' : '20px',
              position: 'relative',
              zIndex: 5,
              textShadow: `1px 1px 0px ${darkenColor(solidBg, 0.15)}`,
            }}
          >
            {profile.display_name || `@${profile.username}`}
          </h3>

          {/* Bio */}
          {profile.bio && (
            <p className="mt-1 text-center px-2 line-clamp-2" style={{ color: subtextColor, fontSize: '0.55rem' }}>
              {profile.bio}
            </p>
          )}

          {/* Social Icons */}
          {profile.social_links?.length > 0 && (
            <div className="flex items-center justify-center gap-2 mt-2">
              {profile.social_links.filter(s => s.is_active).slice(0, 4).map((s) => (
                <div key={s.id} className="w-4 h-4 rounded-full" style={{ backgroundColor: lightBg ? darkenColor(solidBg, 0.4) : themeColor }} />
              ))}
            </div>
          )}

          {/* Tab Switcher */}
          {(activeLinks.length > 0 || activeProducts.length > 0) && (
            <div
              className="flex rounded-full mt-2.5 p-0.5 mx-auto w-fit"
              style={{ backgroundColor: lightBg ? darkenColor(solidBg, 0.15) : 'rgba(255,255,255,0.1)' }}
            >
              {activeLinks.length > 0 && (
                <div className="px-3 py-1 rounded-full" style={{ backgroundColor: lightBg ? '#ffffff' : themeColor, fontSize: '0.5rem', fontWeight: 600, color: lightBg ? '#000' : '#fff' }}>Links</div>
              )}
              {activeProducts.length > 0 && (
                <div className="px-3 py-1 rounded-full" style={{ backgroundColor: activeLinks.length === 0 ? (lightBg ? '#ffffff' : themeColor) : 'transparent', fontSize: '0.5rem', fontWeight: 600, color: activeLinks.length === 0 ? (lightBg ? '#000' : '#fff') : subtextColor }}>{catalogTitle}</div>
              )}
            </div>
          )}

          {/* Links */}
          {(profile.link_layout === 'grid') ? (
            <div className="w-full mt-2.5 px-1 grid grid-cols-2 gap-1">
              {activeLinks.slice(0, 4).map((link) => {
                const brand = getBrandForLink(link);
                return (
                  <div
                    key={link.id}
                    className="aspect-square flex flex-col items-center justify-center gap-1 p-1"
                    style={{
                      background: brand ? (brand.gradient || brand.color) : cardBg,
                      border: brand ? 'none' : `1.5px solid ${borderColor}`,
                      borderRadius: '8px',
                    }}
                  >
                    {brand ? (
                      <div className="w-4 h-4 flex items-center justify-center"
                        dangerouslySetInnerHTML={{ __html: brand.svg.replace('viewBox', `width="16" height="16" viewBox`) }} />
                    ) : link.icon ? (
                      <div className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: lightenColor(solidBg, 0.1) }}>
                        <span style={{ fontSize: '0.4rem' }}>{link.icon}</span>
                      </div>
                    ) : null}
                    <span className="text-center truncate w-full" style={{ color: brand ? (brand.textColor || '#fff') : textColor, fontSize: '0.4rem', fontWeight: 500 }}>{link.title}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="w-full mt-2.5 space-y-1.5 px-1">
              {activeLinks.slice(0, 3).map((link) => (
                <div
                  key={link.id}
                  className="w-full py-1.5 px-2.5 rounded-full flex items-center gap-1.5"
                  style={{ backgroundColor: cardBg, border: `1.5px solid ${borderColor}` }}
                >
                  {link.icon && (
                    <div className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: lightenColor(solidBg, 0.1) }}>
                      <span style={{ fontSize: '0.55rem' }}>{link.icon}</span>
                    </div>
                  )}
                  <span className="flex-1 text-center truncate" style={{ color: textColor, fontSize: '0.55rem', fontWeight: 500 }}>{link.title}</span>
                </div>
              ))}
            </div>
          )}

          {activeLinks.length > 3 && (
            <p className="mt-1 text-center" style={{ color: subtextColor, fontSize: '0.45rem' }}>+{activeLinks.length - 3} more</p>
          )}

          {/* Products Grid */}
          {activeProducts.length > 0 && (() => {
            const allImgs = [];
            activeProducts.forEach(p => {
              const imgs = p.images?.length > 0 ? p.images : (p.image_url ? [p.image_url] : []);
              if (imgs.length > 0) {
                imgs.forEach(img => allImgs.push({ img, title: p.title }));
              } else {
                allImgs.push({ img: null, title: p.title });
              }
            });
            return (
              <div className="w-full mt-2.5 px-1">
                <div className="grid grid-cols-3 gap-0.5 rounded-lg overflow-hidden" style={{ border: `1.5px solid ${borderColor}` }}>
                  {allImgs.map((item, i) => (
                    <div key={i} className="aspect-square" style={{ backgroundColor: cardBg }}>
                      {item.img ? (
                        <img src={resolveMediaUrl(item.img)} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center p-1">
                          <span style={{ color: textColor, fontSize: '0.4rem' }}>{item.title}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {activeLinks.length === 0 && activeProducts.length === 0 && !profile.avatar_url && (
            <div className="mt-4 text-center">
              <p style={{ color: subtextColor, fontSize: '0.55rem' }}>Add your hero image and links</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePreview;
